import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, type Variants } from "framer-motion";
import { Check, Sparkles, X, Loader2, Crown, LogOut } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

import { analytics } from "@/services/analyticsService";

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  isMandatory?: boolean;
}

export const PricingModal: React.FC<PricingModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle = "Desbloqueie todo o poder da inteligência preditiva para validar ideias e antecipar objeções com personas sintéticas.",
  isMandatory = false,
}) => {
  const { profile, signOut } = useAuth();
  const [billingInterval, setBillingInterval] = useState<"month" | "year">("year");
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const isTrialUser = profile?.subscription_tier === "trial" || !profile?.subscription_tier || !profile?.subscription_expires_at;

  const resolvedTitle = title || (
    isMandatory
      ? (isTrialUser
          ? "Seu período de trial terminou. Escolha um plano abaixo para desbloquear seu acesso imediatamente."
          : "Sua assinatura expirou. Escolha um plano abaixo para desbloquear seu acesso imediatamente.")
      : "Escolha o plano ideal para continuar"
  );

  useEffect(() => {
    if (isOpen) {
      analytics.track("pricing_modal_opened", {
        trigger: isMandatory ? "mandatory_expiry" : "user_action",
        user_tier: profile?.subscription_tier || "trial",
      });

      document.body.style.overflow = "hidden";
      const handleEsc = (e: KeyboardEvent) => {
        if (e.key === "Escape" && !isMandatory) {
          analytics.track("pricing_modal_closed", { trigger_source: "escape_key" });
          onClose();
        }
      };
      window.addEventListener("keydown", handleEsc);
      return () => {
        document.body.style.overflow = "unset";
        window.removeEventListener("keydown", handleEsc);
      };
    }
  }, [isOpen, onClose, isMandatory, profile?.subscription_tier]);

  // Mapeamento dos Price IDs da Stripe (Live)
  const PLANS = [
    {
      id: "basic",
      name: "Basic",
      description: "Para profissionais autônomos e pequenos projetos que precisam de validações pontuais.",
      monthlyPrice: "29,90",
      annualMonthlyPrice: "24,91",
      annualTotalPrice: "299,00",
      priceIdMonth: "price_1U5AGNBrxpKuyvXsQwVz1nQX",
      priceIdYear: "price_1U5AJ7BrxpKuyvXsK7SsVczX",
      popular: false,
      features: [
        "Até 3 personas sintéticas ativas",
        "Até 3 contextos personalizados",
        "Simulador de conversas ilimitado",
        "Suporte básico",
      ],
      notIncluded: [
        "Extração e análise de insights por IA",
        "Análise de sentimento das conversas",
      ],
    },
    {
      id: "pro",
      name: "Pro",
      description: "O mais escolhido por estrategistas, copywriters e equipes de produto para validações contínuas.",
      monthlyPrice: "79,90",
      annualMonthlyPrice: "66,58",
      annualTotalPrice: "799,00",
      priceIdMonth: "price_1U5AQSBrxpKuyvXsVlRhUN6S",
      priceIdYear: "price_1U5AQSBrxpKuyvXs8XjYQMBb",
      popular: true,
      features: [
        "Tudo do Plano Basic",
        "Personas sintéticas ilimitadas",
        "Upload ilimitado de contextos personalizados",
        "Extração e análise de Insights com IA",
        "Histórico ilimitado de conversas e testes",
        "Suporte dedicado por e-mail",
      ],
      notIncluded: [],
    },
    {
      id: "business",
      name: "Business",
      description: "Para agências, consultorias e empresas que desejam múltiplos acessos e atendimento dedicado.",
      monthlyPrice: "249,90",
      annualMonthlyPrice: "183,33",
      annualTotalPrice: "2.200,00",
      priceIdMonth: "price_1U5AWrBrxpKuyvXsFztZzm3L",
      priceIdYear: "price_1U5AWrBrxpKuyvXs3PMVNeBs",
      popular: false,
      features: [
        "Tudo do Plano Pro",
        "Múltiplos acessos e contas de equipe",
        "Acesso antecipado a novas funcionalidades",
        "Workspaces colaborativos para projetos e clientes",
        "Consultoria inicial para modelagem de personas",
        "Treinamento de onboarding personalizado",
        "Suporte prioritário garantido 24/7",
      ],
      notIncluded: [],
    },
  ];

  const handleIntervalToggle = (newInterval: "month" | "year") => {
    setBillingInterval(newInterval);
    analytics.track("pricing_interval_toggled", { billing_interval: newInterval });
  };

  const handleSubscribe = async (plan: typeof PLANS[0]) => {
    try {
      setLoadingPlan(plan.id);
      setErrorMsg(null);

      const priceValue = billingInterval === "year"
        ? parseFloat(plan.annualTotalPrice.replace(".", "").replace(",", "."))
        : parseFloat(plan.monthlyPrice.replace(",", "."));

      analytics.track("pricing_plan_selected", {
        plan_id: plan.id,
        billing_interval: billingInterval,
        price_value: priceValue,
      });

      const priceId = billingInterval === "year" ? plan.priceIdYear : plan.priceIdMonth;

      analytics.track("checkout_redirect_started", {
        plan_id: plan.id,
        billing_interval: billingInterval,
        price_id: priceId,
      });

      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: {
          priceId,
          successUrl: `${window.location.origin}/configuracoes?status=success`,
          cancelUrl: `${window.location.origin}/configuracoes?status=canceled`,
        },
      });

      if (error) {
        throw error;
      }

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("URL de checkout não retornada pelo servidor.");
      }
    } catch (err: unknown) {
      const error = err as Error;
      console.error("Erro ao iniciar checkout:", error);
      setErrorMsg(error.message || "Erro ao conectar com a Stripe. Tente novamente.");
      setLoadingPlan(null);
    }
  };

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const cardVariants: Variants = {
    hidden: { opacity: 0, y: 16 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24,
      },
    },
  };

  if (!isOpen && typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
          {/* Backdrop com blur suave e elegante que cobre 100% da tela */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-md"
            onClick={() => {
              if (!isMandatory) onClose();
            }}
          />

          {/* Modal Container */}
          <div className="relative min-h-screen flex items-center justify-center p-3 sm:p-5 md:p-8 pointer-events-none">
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="pricing-modal-title"
              initial={{ opacity: 0, scale: 0.94, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 20 }}
              transition={{ type: "spring", stiffness: 350, damping: 28 }}
              className="relative w-full max-w-5xl bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden my-auto pointer-events-auto"
            >
              {/* Botão de Fechar ou Sair da Conta */}
              {isMandatory ? (
                <button
                  onClick={() => signOut()}
                  className="absolute top-4 right-4 sm:top-5 sm:right-5 z-20 px-3 py-1.5 text-xs font-bold text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-xl border border-slate-200 transition-all flex items-center gap-1.5 cursor-pointer shadow-sm"
                  aria-label="Sair da Conta"
                  title="Sair da Conta"
                >
                  <LogOut size={14} />
                  <span>Sair da Conta</span>
                </button>
              ) : (
                <button
                  onClick={() => {
                    analytics.track("pricing_modal_closed", { trigger_source: "close_button" });
                    onClose();
                  }}
                  className="absolute top-4 right-4 sm:top-5 sm:right-5 z-20 p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors cursor-pointer"
                  aria-label="Fechar"
                >
                  <X size={20} />
                </button>
              )}

              {/* Header Section */}
              <div className="px-5 sm:px-8 pt-7 sm:pt-9 pb-4 sm:pb-5 text-center max-w-2xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.05, duration: 0.2 }}
                  className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-xs font-semibold uppercase tracking-wider mb-3"
                >
                  <Sparkles size={14} /> Planos & Assinaturas
                </motion.div>
                
                <h2 id="pricing-modal-title" className="text-xl sm:text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight leading-tight">
                  {resolvedTitle}
                </h2>
                
                <p className="text-slate-600 text-xs sm:text-sm mt-2 leading-relaxed max-w-xl mx-auto">
                  {subtitle}
                </p>

                {/* Toggle Mensal / Anual com animação fluida */}
                <div className="flex items-center justify-center gap-3 mt-5">
                  <span
                    onClick={() => handleIntervalToggle("month")}
                    className={`text-xs sm:text-sm font-semibold transition-colors cursor-pointer ${
                      billingInterval === "month" ? "text-slate-900" : "text-slate-400 hover:text-slate-600"
                    }`}
                  >
                    Mensal
                  </span>
                  
                  <button
                    onClick={() => handleIntervalToggle(billingInterval === "year" ? "month" : "year")}
                    className="relative w-14 h-8 bg-slate-200 rounded-full p-1 transition-colors hover:bg-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer flex items-center"
                    aria-label="Alternar plano mensal ou anual"
                  >
                    <motion.div
                      layout
                      transition={{ type: "spring", stiffness: 600, damping: 35 }}
                      className={`w-6 h-6 rounded-full shadow-md ${
                        billingInterval === "year" ? "ml-auto bg-indigo-600" : "mr-auto bg-slate-700"
                      }`}
                    />
                  </button>
                  
                  <div
                    onClick={() => handleIntervalToggle("year")}
                    className="flex items-center gap-1.5 cursor-pointer"
                  >
                    <span
                      className={`text-xs sm:text-sm font-semibold transition-colors ${
                        billingInterval === "year" ? "text-slate-900" : "text-slate-400 hover:text-slate-600"
                      }`}
                    >
                      Anual
                    </span>
                    <motion.span
                      initial={{ scale: 0.9 }}
                      animate={{ scale: [0.9, 1.05, 1] }}
                      transition={{ duration: 0.3 }}
                      className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[11px] sm:text-xs font-bold rounded-full"
                    >
                      2 Meses Grátis
                    </motion.span>
                  </div>
                </div>

                {errorMsg && (
                  <div className="mt-4 p-3 bg-red-50 text-red-700 border border-red-200 rounded-xl text-xs sm:text-sm font-medium">
                    {errorMsg}
                  </div>
                )}
              </div>

              {/* Plan Cards Grid com animações escalonadas (stagger) */}
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="show"
                className="px-4 sm:px-6 md:px-8 pb-6 sm:pb-8 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 md:gap-6"
              >
                {PLANS.map((plan) => {
                  const isCurrent = profile?.subscription_tier === plan.id;
                  const isLoading = loadingPlan === plan.id;

                  return (
                    <motion.div
                      key={plan.id}
                      variants={cardVariants}
                      whileHover={{ y: -4, transition: { duration: 0.2 } }}
                      className={`relative flex flex-col justify-between rounded-2xl p-5 sm:p-6 transition-shadow ${
                        plan.popular
                          ? "bg-slate-900 text-white shadow-xl ring-2 ring-indigo-500"
                          : "bg-slate-50 text-slate-900 border border-slate-200 hover:border-slate-300 hover:shadow-md"
                      }`}
                    >
                      {plan.popular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-indigo-600 text-white text-[11px] font-extrabold uppercase tracking-wider rounded-full shadow-md flex items-center gap-1">
                          <Crown size={12} /> Mais Escolhido
                        </div>
                      )}

                      <div>
                        <h3 className="text-lg sm:text-xl font-bold">{plan.name}</h3>
                        <p className={`text-xs mt-1.5 min-h-[32px] ${plan.popular ? "text-slate-300" : "text-slate-500"}`}>
                          {plan.description}
                        </p>

                        {/* Pricing Display com Animação de Troca */}
                        <div className="mt-4 sm:mt-5">
                          <AnimatePresence mode="wait">
                            <motion.div
                              key={billingInterval}
                              initial={{ opacity: 0, y: -4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 4 }}
                              transition={{ duration: 0.15 }}
                            >
                              <div className="flex items-baseline gap-1">
                                <span className="text-xs font-semibold">R$</span>
                                <span className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                                  {billingInterval === "year" ? plan.annualMonthlyPrice : plan.monthlyPrice}
                                </span>
                                <span className={`text-xs ${plan.popular ? "text-slate-300" : "text-slate-500"}`}>
                                  /mês
                                </span>
                              </div>
                              {billingInterval === "year" && (
                                <p className={`text-[11px] sm:text-xs mt-1 font-medium ${plan.popular ? "text-indigo-300" : "text-slate-500"}`}>
                                  Cobrado anualmente: R$ {plan.annualTotalPrice}/ano
                                </p>
                              )}
                            </motion.div>
                          </AnimatePresence>
                        </div>

                        {/* Features List */}
                        <div className="mt-4 pt-4 sm:mt-5 sm:pt-5 border-t border-slate-200/20 space-y-2.5">
                          {plan.features.map((feat, idx) => (
                            <div key={idx} className="flex items-start gap-2 text-xs">
                              <Check className={plan.popular ? "text-indigo-400 shrink-0 mt-0.5" : "text-emerald-600 shrink-0 mt-0.5"} size={14} />
                              <span className={plan.popular ? "text-slate-200" : "text-slate-700"}>{feat}</span>
                            </div>
                          ))}
                          {plan.notIncluded.map((notFeat, idx) => (
                            <div key={`not-${idx}`} className="flex items-start gap-2 text-xs opacity-50">
                              <X className="text-slate-400 shrink-0 mt-0.5" size={14} />
                              <span className="text-slate-400 line-through">{notFeat}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Action Button */}
                      <div className="mt-6 sm:mt-8">
                        <button
                          disabled={isLoading || isCurrent}
                          onClick={() => handleSubscribe(plan)}
                          className={`w-full py-2.5 sm:py-3 px-4 rounded-xl font-bold text-xs sm:text-sm transition-all flex items-center justify-center gap-2 shadow-sm cursor-pointer ${
                            isCurrent
                              ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                              : plan.popular
                              ? "bg-indigo-600 hover:bg-indigo-500 text-white shadow-indigo-600/30 hover:shadow-indigo-600/50"
                              : "bg-slate-900 hover:bg-slate-800 text-white"
                          }`}
                        >
                          {isLoading ? (
                            <>
                              <Loader2 size={15} className="animate-spin" />
                              Redirecionando...
                            </>
                          ) : isCurrent ? (
                            "Seu Plano Atual"
                          ) : (
                            `Assinar ${plan.name}`
                          )}
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>

              {/* Footer Guarantee */}
              <div className="bg-slate-50 px-5 sm:px-8 py-3.5 sm:py-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between text-[11px] sm:text-xs text-slate-500 gap-2 text-center sm:text-left">
                <span>🔒 Pagamento seguro processado criptograficamente pela <strong>Stripe</strong>.</span>
                <span>Cancele quando quiser com 1 clique direto pelo Customer Portal.</span>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};
